// src/pages/qc/PageQCBfAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { BfAnalysisModal } from "./BfAnalysisModal";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { currentMonthKey } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

// ── Verdict helpers ───────────────────────────────────────────────────────────

function fvNum(value, min, max) {
  if (value == null) return "SKIP";
  if (min == null && max == null) return "SKIP";
  const v = parseFloat(value);
  if (min != null && v < parseFloat(min)) return "FAIL";
  if (max != null && v > parseFloat(max)) return "FAIL";
  return "PASS";
}

function fvText(measured, spec) {
  if (!measured) return "SKIP";
  if (!spec)     return "SKIP";
  return measured.trim().toLowerCase() === spec.trim().toLowerCase() ? "PASS" : "FAIL";
}

function rowVerdict(row, spec) {
  if (!spec) return "NS";
  const verdicts = [
    fvText(row.appearance, spec.appearance),
    fvText(row.color,      spec.color),
    fvNum(row.density,     spec.densityMin, spec.densityMax),
    // ERBP must be ≥ erbpMin (min only)
    fvNum(row.erbp,        spec.erbpMin,    null),
  ].filter(v => v !== "SKIP");

  if (verdicts.length === 0) return "NS";
  if (verdicts.includes("FAIL")) return "FAIL";
  return "PASS";
}

// ── UI components ─────────────────────────────────────────────────────────────

const VERDICT_CFG = {
  PASS: { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"PASS" },
  FAIL: { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"FAIL" },
  NS:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"N/S"  },
};

function VerdictBadge({ verdict }) {
  const c = VERDICT_CFG[verdict] ?? VERDICT_CFG.NS;
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

const ROW_BG    = { PASS:"#f0fdf4", FAIL:"#fff1f2", NS:"#fefce8" };
const FAIL_CELL = { background:"#fee2e2", color:"#991b1b", fontWeight:600 };

function FC({ verdict, children }) {
  return <td style={verdict === "FAIL" ? FAIL_CELL : {}}>{children ?? "—"}</td>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PageQCBfAnalysis({ myPerms }) {
  const showToast = useToast();
  const [rows,    setRows]    = useState([]);
  const [specs,   setSpecs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [modal,   setModal]   = useState(false);

  const canEdit   = myPerms?.includes("QC_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    Promise.all([api.fetchBfAnalyses(), api.fetchBfSpecs()])
      .then(([analyses, bfSpecs]) => { setRows(analyses); setSpecs(bfSpecs); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const specMap = useMemo(() => {
    const m = {};
    specs.forEach(s => { m[s.productId] = s; });
    return m;
  }, [specs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.date || "").startsWith(monthKey) &&
      (r.productId.toLowerCase().includes(q) ||
       (r.batchNo || "").toLowerCase().includes(q))
    );
  }, [rows, search, monthKey]);

  async function handleCreate(body) {
    const created = await api.createBfAnalysis(body);
    setRows(old => [created, ...old]);
    showToast("Analysis saved");
    setModal(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis record?")) return;
    await api.deleteBfAnalysis(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Record deleted", "info");
  }

  const colCount = 8 + (canDelete ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Brake Fluid Analysis</div>
          <div className="page-subtitle">Pass / Fail verdict against brake fluid specifications</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Analysis</button>
          </div>
        )}
      </div>

      <div className="action-bar">
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search product or batch…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:240 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Product</th>
                <th>Batch No</th>
                <th>Appearance</th>
                <th>Color</th>
                <th>Density (g/cc)</th>
                <th>ERBP (°C)</th>
                <th>Verdict</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const spec    = specMap[row.productId];
                const verdict = rowVerdict(row, spec);
                const bg      = ROW_BG[verdict] ?? "#fff";

                const appV = fvText(row.appearance, spec?.appearance);
                const colV = fvText(row.color,      spec?.color);
                const denV = fvNum(row.density,     spec?.densityMin, spec?.densityMax);
                const erbV = fvNum(row.erbp,        spec?.erbpMin,    null);

                return (
                  <tr key={row.id} style={{ background: bg }}>
                    <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                    <td className="fw-600">{row.productId}</td>
                    <td>{row.batchNo || "—"}</td>
                    <FC verdict={appV}>{row.appearance}</FC>
                    <FC verdict={colV}>{row.color}</FC>
                    <FC verdict={denV}>{row.density != null ? String(row.density) : null}</FC>
                    <FC verdict={erbV}>{row.erbp != null ? `${row.erbp}°C` : null}</FC>
                    <td><VerdictBadge verdict={verdict} /></td>
                    {canDelete && (
                      <td>
                        <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <BfAnalysisModal
            specs={specs}
            onSave={handleCreate}
            onCancel={() => setModal(false)}
          />
        </div>
      )}
    </>
  );
}
